import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { HelpTip } from "@/components/HelpTip";
import { PalletFinalSale } from "@/components/PalletFinalSale";
import { useCart, lineTotals, memberSavings } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useReferral } from "@/context/ReferralContext";
import { api, fmtMoney, SQUARE_APP_ID, SQUARE_LOCATION_ID } from "@/lib/api";
import { Truck, Store, Tag, Loader2, ShieldCheck, Check, BadgeCheck, Sparkles, Zap, Phone, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { STORE_ADDRESS } from "@/constants/store";

export default function CheckoutPage() {
  const { items, subtotal, savings, clear } = useCart();
  const { user } = useAuth();
  const { referral } = useReferral();
  const navigate = useNavigate();

  const [fulfillment, setFulfillment] = useState("delivery");
  const [rush, setRush] = useState(false);
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    delivery_address: "",
    delivery_city: "",
    delivery_state: "MI",
    delivery_zip: "",
    notes: "",
  });
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteRequestSent, setQuoteRequestSent] = useState(false);
  const [slots, setSlots] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [holdOrder, setHoldOrder] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Spec section 8: block checkout until the customer acknowledges the return policy
  const [returnPolicyAck, setReturnPolicyAck] = useState(false);
  const [palletAck, setPalletAck] = useState(false);

  const hasPalletInCart = items.some((i) => i.is_pallet || i.product_id === "PALLET_BUILDER");
  const shipItems = items.filter((i) => i.ships_only);
  const storeItems = items.filter((i) => !i.ships_only);
  const shipsOnlyCart = shipItems.length > 0 && storeItems.length === 0;
  const mixedCart = shipItems.length > 0 && storeItems.length > 0;
  const localSubtotal = +storeItems.reduce((s, i) => s + lineTotals(i).total, 0).toFixed(2);
  const canPay = returnPolicyAck && (!hasPalletInCart || palletAck);

  useEffect(() => {
    if (items.length === 0) {
      navigate("/cart");
    }
  }, [items, navigate]);

  // Cart changes invalidate the last quote so pricing can't drift.
  useEffect(() => {
    setDeliveryQuote(null);
    setQuoteRequestSent(false);
  }, [hasPalletInCart, items.length]);

  // Rush toggle auto-recalculates the fee (never silently wipes it)
  useEffect(() => {
    setDeliveryQuote(null);
    setQuoteRequestSent(false);
    if (fulfillment === "delivery" && form.delivery_address && form.delivery_city && form.delivery_state && form.delivery_zip) {
      quoteDelivery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rush]);

  // Phase 15e — load Tue/Thu/Sat delivery slots
  useEffect(() => {
    if (fulfillment !== "delivery") return;
    api
      .get("/delivery/slots")
      .then((r) => {
        const s = r.data.slots || [];
        setSlots(s);
        setDeliveryDate((cur) => (cur && s.some((x) => x.date === cur) ? cur : ""));
      })
      .catch(() => {});
  }, [fulfillment]);

  const memberDiscount = memberSavings(items, !!user);
  const swapFee = items.some((i) => i.swap_request) ? 99 : 0;
  const taxableBase = subtotal + swapFee - memberDiscount - (appliedDiscount?.amount || 0);
  const tax = Math.max(0, +(taxableBase * 0.06).toFixed(2));
  const deliveryFee = fulfillment === "delivery" && deliveryQuote?.available && !deliveryQuote?.requires_quote
    ? Number(deliveryQuote.fee || 0)
    : 0;
  const total = +(taxableBase + tax + deliveryFee).toFixed(2);

  const quoteDelivery = async () => {
    if (!form.delivery_address || !form.delivery_city || !form.delivery_state || !form.delivery_zip) {
      toast.error("Enter your full delivery address first");
      return;
    }
    setQuoteLoading(true);
    setQuoteRequestSent(false);
    try {
      const { data } = await api.post("/delivery/quote", {
        address: form.delivery_address,
        city: form.delivery_city,
        state: form.delivery_state,
        zip_code: form.delivery_zip,
        cart_subtotal: localSubtotal,
        has_pallet: hasPalletInCart,
        regular_subtotal: hasPalletInCart
          ? storeItems.filter((it) => !(it.product_id === "PALLET_BUILDER" || it.is_pallet)).reduce((s, it) => s + it.price * it.quantity, 0)
          : 0,
        rush,
      });
      if (!data.available) {
        toast.error(data.reason);
        setDeliveryQuote(null);
        return;
      }
      setDeliveryQuote(data);
      if (data.requires_quote) {
        toast.message("This trip needs a custom quote — request one below.");
      } else if (data.free_qualified) {
        toast.success("FREE delivery unlocked!");
      } else {
        toast.success(`Delivery: $${Number(data.fee).toFixed(2)}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not calculate delivery");
    } finally {
      setQuoteLoading(false);
    }
  };

  const requestCustomQuote = async () => {
    if (!form.customer_name || !form.customer_email) {
      toast.error("Please add your name and email first");
      return;
    }
    try {
      await api.post("/delivery/request-quote", {
        address: form.delivery_address,
        city: form.delivery_city,
        state: form.delivery_state,
        zip_code: form.delivery_zip,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        cart_summary: items.map((i) => `${i.quantity}× ${i.name}`).join(", "),
        has_pallet: hasPalletInCart,
        miles: deliveryQuote?.miles ?? null,
      });
      setQuoteRequestSent(true);
      toast.success("Got it — we'll be in touch within one business day.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your request");
    }
  };

  const applyCode = async () => {
    if (!discountCode.trim()) return;
    try {
      const { data } = await api.post(
        `/discounts/validate?code=${encodeURIComponent(discountCode.trim())}`
      );
      if (subtotal < (data.min_subtotal || 0)) {
        toast.error(`Requires subtotal ≥ ${fmtMoney(data.min_subtotal)}`);
        return;
      }
      // Member discount applies first; discount code stacks on the remainder.
      const baseForCode = Math.max(0, subtotal - memberDiscount);
      let amount =
        data.discount_type === "percent"
          ? +(baseForCode * (data.value / 100)).toFixed(2)
          : Math.min(data.value, baseForCode);
      setAppliedDiscount({ code: data.code, type: data.discount_type, value: data.value, amount });
      toast.success(`Applied code ${data.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    }
  };

  const onCardTokenize = async (token) => {
    if (!form.customer_name || !form.customer_email) {
      toast.error("Please fill in your contact info");
      return;
    }
    const effectiveFulfillment = shipsOnlyCart ? "shipping" : fulfillment;
    const needsAddress = effectiveFulfillment === "delivery" || effectiveFulfillment === "shipping" || shipItems.length > 0;
    if (needsAddress && (!form.delivery_address || !form.delivery_city || !form.delivery_state || !form.delivery_zip)) {
      toast.error(shipItems.length > 0 && effectiveFulfillment !== "delivery" ? "Please complete the shipping address" : "Please complete delivery address");
      return;
    }
    if (effectiveFulfillment === "delivery" && (!deliveryQuote || !deliveryQuote.available || deliveryQuote.requires_quote)) {
      toast.error("Please calculate delivery first (or request a custom quote for long distances)");
      return;
    }
    if (effectiveFulfillment === "delivery" && !rush && !holdOrder && !deliveryDate) {
      toast.error("Please pick a delivery day (Tuesday, Thursday or Saturday)");
      return;
    }
    if (!returnPolicyAck) {
      toast.error("Please acknowledge the return policy to continue");
      return;
    }
    if (hasPalletInCart && !palletAck) {
      toast.error("Please acknowledge the final-sale pallet terms first");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          color: i.color || null,
          pallet_tier: i.pallet_tier || null,
          pallet_items: i.pallet_items || null,
          swap_request: i.swap_request || null,
        })),
        source_id: token.token,
        fulfillment: effectiveFulfillment,
        discount_code: appliedDiscount?.code,
        referral_code: referral?.code || null,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        delivery_address: needsAddress ? form.delivery_address : null,
        delivery_city: needsAddress ? form.delivery_city : null,
        delivery_state: needsAddress ? form.delivery_state : null,
        delivery_zip: needsAddress ? form.delivery_zip : null,
        rush: effectiveFulfillment === "delivery" && rush && !hasPalletInCart,
        hold: holdOrder,
        delivery_date: effectiveFulfillment === "delivery" && !rush && !holdOrder ? deliveryDate || null : null,
        pickup_date: effectiveFulfillment === "pickup" ? pickupDate || null : null,
        notes: form.notes,
      };
      const { data } = await api.post("/checkout/pay", payload);
      clear();
      toast.success("Payment successful!");
      navigate(`/order/${data.order_number}?email=${encodeURIComponent(data.customer_email)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12" data-testid="checkout-page">
      <div className="ipv-eyebrow text-[#0A1C40]">Checkout</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">
        Place your order
      </h1>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-12">
        <div className="space-y-10">
          {/* Fulfillment */}
          {mixedCart && (
            <div className="border-2 border-[#0A1C40]/30 bg-blue-50 p-4 text-sm" data-testid="mixed-cart-notice">
              <b>Items in this order may arrive separately.</b> Store pickup and local delivery apply only to items
              marked "Available in store now" — your ships-to-you items travel by carrier to the shipping address below.
            </div>
          )}
          {shipsOnlyCart ? (
            <section data-testid="ships-only-fulfillment">
              <div className="ipv-eyebrow mb-4">1. Fulfillment</div>
              <div className="border-2 border-[#0A1C40] bg-blue-50 p-5">
                <div className="font-display font-bold flex items-center gap-2"><Truck className="w-5 h-5 text-[#0A1C40]" /> Ships to you</div>
                <div className="text-sm text-gray-600 mt-1">
                  Everything in this order ships by standard carrier to your address — no pickup or local delivery on these items. Timing follows each item's listed shipping time.
                </div>
              </div>
            </section>
          ) : (
          <section>
            <div className="ipv-eyebrow mb-4">1. Fulfillment{mixedCart ? " — items from our store" : ""}</div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "pickup", Icon: Store, title: "In-store Pickup", sub: `${STORE_ADDRESS.short} · Free` },
                {
                  id: "delivery",
                  Icon: Truck,
                  title: "Local Delivery",
                  sub: hasPalletInCart
                    ? "Pallet delivery — flat rate"
                    : "FREE on $299+ within 10 mi",
                },
              ].map(({ id, Icon, title, sub }) => {
                const active = fulfillment === id;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFulfillment(id)}
                    className={`relative border-2 p-5 text-left select-none [touch-action:manipulation] active:scale-[0.98] transition-transform ${
                      active ? "border-[#0A1C40] bg-blue-50" : "border-gray-200"
                    }`}
                    data-testid={`fulfillment-${id}`}
                  >
                    <span
                      className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        active ? "border-[#0A1C40] bg-[#0A1C40]" : "border-gray-300 bg-white"
                      }`}
                    >
                      {active && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </span>
                    <Icon className="w-5 h-5 text-[#0A1C40] mb-3" />
                    <div className="font-display font-bold">{title}</div>
                    <div className="text-sm text-gray-600 mt-1">{sub}</div>
                    {active && (
                      <div className="mt-2 inline-block bg-[#0A1C40] text-white text-[10px] font-bold tracking-widest uppercase px-2 py-0.5">
                        Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
          )}

          {/* Contact */}
          <section>
            <div className="ipv-eyebrow mb-4">2. Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <input className="input-base col-span-2" placeholder="Full name" value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="ck-name" />
              <input className="input-base" placeholder="Email" type="email" value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="ck-email" />
              <input className="input-base" placeholder="Phone" value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="ck-phone" />
            </div>
          </section>

          {/* Shipping address for ships-to-you items (shipping-only carts, or mixed carts with pickup) */}
          {(shipsOnlyCart || (shipItems.length > 0 && fulfillment === "pickup")) && (
            <section data-testid="shipping-address-section">
              <div className="ipv-eyebrow mb-4">3. Shipping address {mixedCart ? "(ships-to-you items)" : ""}</div>
              <div className="grid grid-cols-6 gap-3">
                <input className="input-base col-span-6" placeholder="Street address" value={form.delivery_address}
                  onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} data-testid="ck-ship-address" />
                <input className="input-base col-span-3" placeholder="City" value={form.delivery_city}
                  onChange={(e) => setForm({ ...form, delivery_city: e.target.value })} data-testid="ck-ship-city" />
                <input className="input-base col-span-1" placeholder="State" value={form.delivery_state}
                  onChange={(e) => setForm({ ...form, delivery_state: e.target.value })} data-testid="ck-ship-state" />
                <input className="input-base col-span-2" placeholder="ZIP" value={form.delivery_zip}
                  onChange={(e) => setForm({ ...form, delivery_zip: e.target.value })} data-testid="ck-ship-zip" />
              </div>
            </section>
          )}

          {/* Delivery address */}
          {fulfillment === "delivery" && !shipsOnlyCart && (
            <section>
              <div className="ipv-eyebrow mb-4">3. Delivery Address</div>
              <div className="grid grid-cols-6 gap-3">
                <input className="input-base col-span-6" placeholder="Street address" value={form.delivery_address}
                  onChange={(e) => { setForm({ ...form, delivery_address: e.target.value }); setDeliveryQuote(null); }} data-testid="ck-address" />
                <input className="input-base col-span-3" placeholder="City" value={form.delivery_city}
                  onChange={(e) => { setForm({ ...form, delivery_city: e.target.value }); setDeliveryQuote(null); }} data-testid="ck-city" />
                <input className="input-base col-span-1" placeholder="State" value={form.delivery_state}
                  onChange={(e) => { setForm({ ...form, delivery_state: e.target.value }); setDeliveryQuote(null); }} data-testid="ck-state" />
                <input className="input-base col-span-2" placeholder="ZIP" value={form.delivery_zip}
                  onChange={(e) => { setForm({ ...form, delivery_zip: e.target.value }); setDeliveryQuote(null); }} data-testid="ck-zip" />
              </div>

              {/* Rush toggle — pallets are NOT eligible */}
              {!hasPalletInCart && (
                <label
                  className={`mt-4 flex items-start gap-3 border-2 p-4 cursor-pointer transition-colors ${
                    rush ? "border-[#C9971E] bg-[#FFC220]/10" : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid="rush-toggle"
                >
                  <input
                    type="checkbox"
                    checked={rush}
                    onChange={(e) => setRush(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#C9971E] shrink-0"
                    data-testid="rush-checkbox"
                  />
                  <div className="flex-1">
                    <div className="font-display font-bold text-sm flex items-center gap-2">
                      <Zap className="w-4 h-4 text-[#C9971E]" /> Rush Delivery — same day or next day
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      +$75.00 · <b>Subject to van availability.</b> We will call to confirm.
                    </div>
                  </div>
                </label>
              )}
              {hasPalletInCart && (
                <div className="mt-4 text-xs text-gray-600 border border-gray-200 px-3 py-2 bg-gray-50" data-testid="pallet-no-rush-note">
                  Pallets ship on their own flat-rate schedule and are not eligible for Rush Delivery.
                </div>
              )}

              {/* Phase 15e — delivery date picker (Tue/Thu/Sat) */}
              {!rush && (
                <div className="mt-5" data-testid="delivery-date-picker">
                  <div className="font-display font-bold text-sm flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-[#0A1C40]" /> Pick your delivery day
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    We deliver <b>Tuesday · Thursday · Saturday</b>. Order by <b>2 PM the day before</b>. Saturday window: 9 AM – 1 PM.
                  </div>
                  {slots.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {slots.map((s) => {
                        const active = deliveryDate === s.date;
                        return (
                          <button
                            key={s.date}
                            type="button"
                            aria-pressed={active}
                            onClick={() => setDeliveryDate(s.date)}
                            className={`border-2 p-3 text-left [touch-action:manipulation] active:scale-[0.98] transition-transform ${
                              active ? "border-[#0A1C40] bg-blue-50" : "border-gray-200 hover:border-gray-300"
                            }`}
                            data-testid={`delivery-slot-${s.date}`}
                          >
                            <div className="font-bold text-sm">{s.label}</div>
                            <div className="text-[10px] text-gray-600 mt-0.5">{s.window}</div>
                            {active && (
                              <div className="mt-1 inline-block bg-[#0A1C40] text-white text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5">
                                Selected
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mt-2">Loading available dates…</div>
                  )}
                </div>
              )}
              {rush && (
                <div className="mt-3 text-xs text-[#8a6a00] bg-[#FFC220]/10 border border-[#FFC220] px-3 py-2" data-testid="rush-schedule-note">
                  ⚡ Rush delivery — no date needed. We'll call to confirm same-day or next-day timing.
                </div>
              )}

              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={quoteDelivery}
                  disabled={quoteLoading || !form.delivery_address || !form.delivery_city || !form.delivery_state || !form.delivery_zip}
                  className="btn-outline inline-flex items-center gap-2"
                  data-testid="calc-delivery-btn"
                >
                  {quoteLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Calculate delivery fee
                </button>
                <HelpTip
                  testId="help-delivery-calc"
                  text="We measure real driving miles from our store. Within 10 mi: FREE on $299+ orders, otherwise $39. 10–25 mi: $49. 25–40 mi: $49 + $2/mile past 25. Pallets are always a flat $75 (≤10 mi) or $99 (≤25 mi) and never count toward the free threshold. Past 40 mi (25 for pallets) we quote the trip personally."
                />
              </div>

              {deliveryQuote?.available && !deliveryQuote?.requires_quote && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 text-sm space-y-2" data-testid="delivery-quote-result">
                  <div className="flex items-center gap-2 font-semibold text-[#0A1C40]">
                    <Truck className="w-4 h-4" />
                    {deliveryQuote.free_qualified ? (
                      <span>FREE delivery · {deliveryQuote.miles} mi from our store</span>
                    ) : (
                      <span>{deliveryQuote.miles} mi → ${Number(deliveryQuote.fee).toFixed(2)} total delivery</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-700">{deliveryQuote.breakdown}</div>
                  {deliveryQuote.nudge_amount > 0 && !hasPalletInCart && (
                    <div className="text-xs bg-[#FFC220]/20 border border-[#C9971E]/40 px-3 py-2 font-semibold text-[#5b4400] mt-1" data-testid="free-delivery-nudge">
                      Add ${Number(deliveryQuote.nudge_amount).toFixed(2)} more to qualify for FREE delivery.
                    </div>
                  )}
                </div>
              )}

              {deliveryQuote?.available && deliveryQuote?.requires_quote && !quoteRequestSent && (
                <div className="mt-3 p-4 bg-[#FFC220]/10 border-2 border-[#C9971E] text-sm space-y-3" data-testid="delivery-quote-required">
                  <div className="text-[#0A1C40]">
                    <b>{deliveryQuote.requires_quote_reason}</b>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={requestCustomQuote}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="request-quote-btn"
                    >
                      Request a delivery quote
                    </button>
                    <a href="tel:6164100158" className="text-sm underline inline-flex items-center gap-1 text-[#0A1C40]" data-testid="call-store-link">
                      <Phone className="w-3.5 h-3.5" /> Call 616-410-0158
                    </a>
                  </div>
                </div>
              )}
              {quoteRequestSent && (
                <div className="mt-3 p-3 bg-green-50 border border-green-300 text-sm text-green-800" data-testid="quote-request-sent">
                  Your quote request is in. We&apos;ll email you within one business day.
                </div>
              )}
            </section>
          )}

          {/* Pickup preferred day (optional) */}
          {fulfillment === "pickup" && (
            <section>
              <div className="ipv-eyebrow mb-4">3. Pickup day (optional)</div>
              <div className="text-sm text-gray-600 mb-3">
                Come anytime during store hours — <b>Mon–Sat 10 AM–7 PM · Sun 12–5 PM</b>. Or tell us which day you&apos;re coming:
              </div>
              <input
                type="date"
                className="input-base max-w-xs"
                min={new Date().toISOString().slice(0, 10)}
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                data-testid="ck-pickup-date"
              />
            </section>
          )}

          {/* Discount */}
          <section>
            <div className="ipv-eyebrow mb-4">4. Discount code (optional)</div>
            <div className="flex gap-2">
              <input
                className="input-base"
                placeholder="Enter code"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                data-testid="ck-discount-input"
              />
              <button onClick={applyCode} className="btn-outline" data-testid="ck-apply-discount">
                Apply
              </button>
            </div>
            {appliedDiscount && (
              <div className="mt-3 inline-flex items-center gap-2 text-sm bg-green-50 border border-green-200 px-3 py-1.5">
                <Tag className="w-3.5 h-3.5 text-green-700" />
                <span className="font-bold">{appliedDiscount.code}</span> applied · -{fmtMoney(appliedDiscount.amount)}
                <button onClick={() => setAppliedDiscount(null)} className="text-gray-500 hover:text-black ml-2">✕</button>
              </div>
            )}
          </section>

          <section>
            <div className="ipv-eyebrow mb-4">Notes for our team (optional)</div>
            <textarea
              className="input-base"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="ck-notes"
            />
          </section>
        </div>

        {/* Summary + Payment */}
        <aside className="space-y-6">
          <div className="border border-gray-200 p-6" data-testid="checkout-summary">
            <div className="ipv-eyebrow mb-4">Order Summary</div>
            <div className="space-y-2 text-sm max-h-64 overflow-auto">
              {items.map((it) => {
                const lt = lineTotals(it);
                return (
                  <div key={it.key} className="flex justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="truncate">
                        {it.quantity} × {it.name}
                        {it.color && <span className="text-gray-500"> · {it.color}</span>}
                        {it.fulfillment_type === "order_in" && (
                          <span className="ml-1 text-[10px] font-bold text-[#0A1C40]">~{it.lead_time_days || 7}D</span>
                        )}
                        {lt.dealActive && (
                          <span className="ml-1 text-[10px] font-bold text-[#DC2626]">DEAL</span>
                        )}
                      </div>
                      {it.customization_fee > 0 && (
                        <div className="text-[10px] text-gray-500" data-testid="pallet-line-breakdown">
                          {fmtMoney(it.pallet_price)} pallet (merchandise included) + {fmtMoney(it.customization_fee)} customization fee
                        </div>
                      )}
                      {it.swap_request && (
                        <div className="text-[10px] text-amber-700" data-testid="swap-line-note">
                          Item swap requested (+$99): {it.swap_request}
                        </div>
                      )}
                    </div>
                    <div className="font-bold">{fmtMoney(lt.total)}</div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-gray-200 mt-4 pt-4 space-y-2 text-sm">
              {items.some((i) => i.fulfillment_type === "order_in") && (
                <div className="bg-blue-50 border border-[#0A1C40]/30 px-3 py-2 text-xs text-[#0A1C40] font-semibold" data-testid="checkout-special-notice">
                  📦 Includes special-order item(s) — ready in ~{Math.max(...items.filter((i) => i.fulfillment_type === "order_in").map((i) => i.lead_time_days || 7))} days. We&apos;ll keep you updated.
                </div>
              )}
              <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              {swapFee > 0 && (
                <div className="flex justify-between text-amber-700 font-bold" data-testid="summary-swap-fee">
                  <span>Manifested pallet item swap fee</span><span>{fmtMoney(swapFee)}</span>
                </div>
              )}
              {savings > 0 && (
                <div className="flex justify-between text-green-700 font-bold"><span>Deal savings included</span><span>{fmtMoney(savings)}</span></div>
              )}
              {user && memberDiscount > 0 && (
                <div className="flex justify-between text-green-700 font-bold" data-testid="checkout-member-savings">
                  <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Member 10% off</span>
                  <span>-{fmtMoney(memberDiscount)}</span>
                </div>
              )}
              {!user && (
                <div className="text-xs bg-[#0A1C40] text-white px-2 py-1.5 my-1" data-testid="checkout-member-cta">
                  <Link to="/login" className="font-bold underline">Sign in</Link> to save{" "}
                  <span className="text-[#FFC220] font-bold">10%</span> on non-pallet items.
                </div>
              )}
              {referral?.code && (
                <div className="text-xs bg-[#FFC220] text-[#0A1C40] px-2 py-1.5 my-1 font-semibold inline-flex items-center gap-1.5" data-testid="checkout-referral-banner">
                  <Sparkles className="w-3 h-3" />
                  Referred by <span className="font-bold">{referral.rep_first_name}</span>
                  <span className="text-[10px] text-[#0A1C40]/70">· code {referral.code}</span>
                </div>
              )}
              {appliedDiscount && (
                <div className="flex justify-between text-[#DC2626]"><span>Discount</span><span>-{fmtMoney(appliedDiscount.amount)}</span></div>
              )}
              <div className="flex justify-between"><span>Tax (6%)</span><span>{fmtMoney(tax)}</span></div>
              <div className="flex justify-between">
                <span>Delivery{rush && !hasPalletInCart && !shipsOnlyCart ? " (Rush +$75)" : ""}</span>
                <span>
                  {shipsOnlyCart
                    ? "Carrier shipping"
                    : fulfillment === "pickup"
                    ? "Pickup (free)"
                    : deliveryQuote?.available && !deliveryQuote?.requires_quote
                      ? (deliveryQuote.free_qualified && !rush ? "FREE" : fmtMoney(deliveryFee))
                      : "—"}
                </span>
              </div>
              {mixedCart && (
                <div className="text-[11px] text-gray-500" data-testid="summary-mixed-note">
                  Ships-to-you items arrive separately by carrier — timing per each item's listed shipping time.
                </div>
              )}
              {fulfillment === "delivery" && !rush && deliveryDate && (
                <div className="flex justify-between text-xs text-[#0A1C40] font-bold" data-testid="summary-delivery-date">
                  <span>Delivery day</span>
                  <span>{slots.find((s) => s.date === deliveryDate)?.label || deliveryDate}</span>
                </div>
              )}
              {fulfillment === "pickup" && pickupDate && (
                <div className="flex justify-between text-xs text-[#0A1C40] font-bold" data-testid="summary-pickup-date">
                  <span>Pickup day</span>
                  <span>{pickupDate}</span>
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between items-baseline">
              <div className="font-bold">Total</div>
              <div className="font-display font-black text-3xl" data-testid="checkout-total">{fmtMoney(total)}</div>
            </div>
            {holdOrder && (
              <div className="mt-3 space-y-1 text-sm border-t border-dashed border-gray-300 pt-3" data-testid="hold-summary">
                <div className="flex justify-between font-bold text-[#0A1C40]"><span>Due today (deposit)</span><span>$50.00</span></div>
                <div className="flex justify-between text-gray-600"><span>Balance at pickup/delivery</span><span>{fmtMoney(Math.max(0, total - 50))}</span></div>
              </div>
            )}
          </div>

          {subtotal >= 299 && (
            <div className="border-2 border-[#0A1C40] bg-blue-50 p-4" data-testid="hold-option">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={holdOrder}
                  onChange={(e) => setHoldOrder(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#0A1C40] shrink-0"
                  data-testid="hold-checkbox"
                />
                <span className="text-sm leading-relaxed">
                  <b>Reserve with a $50 deposit</b>{" "}
                  <HelpTip
                    testId="help-hold-order"
                    text="Pay only $50 now — we pull your items off the floor and hold them for 7 days. The $50 counts toward your total. Pay the balance in store or via a payment link we send you. If you don't complete the purchase within 7 days, the deposit is not refunded."
                  />{" "}
                  — we hold your items for <b>7 days</b>. Pay just $50 now (it counts
                  toward your total) and the balance at pickup/delivery. Deposit is non-refundable if the purchase
                  isn&apos;t completed.
                </span>
              </label>
            </div>
          )}

          <div className="border-2 border-[#FFC220] bg-[#FFC220]/10 p-4" data-testid="return-policy-ack">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={returnPolicyAck}
                onChange={(e) => setReturnPolicyAck(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#0A1C40] shrink-0"
                data-testid="return-policy-checkbox"
              />
              <span className="text-sm text-gray-800 leading-relaxed">
                I have read the{" "}
                <Link to="/policies/returns" target="_blank" rel="noopener" className="underline font-bold text-[#0A1C40]" data-testid="return-policy-link">
                  return policy
                </Link>
                . I understand IPVALUE Marketplace issues <b>exchange or store credit only</b> — no cash refunds.
                Certain items are <b>final sale</b> (mattresses, bedding, AS-IS items, personal care, e-bikes once ridden).
              </span>
            </label>
          </div>

          {hasPalletInCart && (
            <div className="space-y-3" data-testid="pallet-ack-section">
              <PalletFinalSale compact />
              <label className="flex items-start gap-3 border-2 border-red-600 bg-white p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={palletAck}
                  onChange={(e) => setPalletAck(e.target.checked)}
                  data-testid="pallet-ack-checkbox"
                />
                <span className="text-sm leading-relaxed">
                  I understand that <b>pallet sales are final</b> and I have reviewed the{" "}
                  <Link to="/policies/pallets" target="_blank" className="underline font-bold">Pallet Sales Policy</Link>.
                </span>
              </label>
            </div>
          )}

          <div className="border border-gray-200 p-6" data-testid="payment-section">
            <div className="ipv-eyebrow mb-4 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#0A1C40]" /> Pay securely · Square
            </div>
            {!returnPolicyAck && (
              <div className="mb-4 text-xs text-[#8a6a00] bg-[#FFC220]/10 border border-[#FFC220] px-3 py-2" data-testid="return-policy-required-hint">
                ⚠ Please tick the return-policy box above before you pay.
              </div>
            )}
            {hasPalletInCart && !palletAck && (
              <div className="mb-4 text-xs text-red-700 bg-red-50 border border-red-500 px-3 py-2" data-testid="pallet-ack-required-hint">
                ⚠ Please acknowledge the final-sale pallet terms above before you pay.
              </div>
            )}
            {submitting && (
              <div className="mb-4 text-sm flex items-center gap-2 text-[#0A1C40]">
                <Loader2 className="w-4 h-4 animate-spin" /> Processing payment...
              </div>
            )}
            <div data-testid="square-card-container">
            <PaymentForm
              applicationId={SQUARE_APP_ID}
              locationId={SQUARE_LOCATION_ID}
              cardTokenizeResponseReceived={onCardTokenize}
            >
              <CreditCard
                buttonProps={{
                  isLoading: submitting,
                  isDisabled: !canPay,
                  css: {
                    backgroundColor: canPay ? "#0A1C40" : "#94a3b8",
                    fontSize: "14px",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    borderRadius: "9999px",
                    "&:hover": { backgroundColor: canPay ? "#152b59" : "#94a3b8" },
                  },
                }}
              >
                Pay {fmtMoney(holdOrder ? 50 : total)}
              </CreditCard>
            </PaymentForm>
            </div>
            <div className="mt-4 text-xs text-gray-500 text-center">
              By placing your order you agree to our{" "}
              <Link to="/policies/delivery" className="underline">delivery</Link> &amp;{" "}
              <Link to="/policies/returns" className="underline">return</Link> policies.
            </div>
            <a
              href="tel:6164100158"
              className="mt-3 flex items-center justify-center gap-2 text-sm font-bold text-[#0A1C40] border-2 border-[#0A1C40]/20 rounded-full py-2.5 hover:border-[#0A1C40] transition-colors"
              data-testid="checkout-call-link"
            >
              <Phone className="w-4 h-4" /> Questions before you pay? Call us: (616) 410-0158
            </a>
          </div>

          <Link to="/cart" className="block text-center text-sm text-gray-600 hover:text-black">
            ← Back to cart
          </Link>
        </aside>
      </div>
    </div>
  );
}
